<template>
  <div id="industrial-map-container" class="full-height">
    <!-- Container principal do mapa -->
    <div class="map-main-container">
      <!-- Área central do mapa -->
      <div class="map-center-area">
        <div class="map-placeholder-content">
          <q-icon name="map" size="3rem" color="primary" />
          <div class="text-h6 text-primary q-mt-md">
            Mapa Industrial Dinâmico
          </div>
          <div class="text-body2 text-grey-7 q-mb-lg">
            Visualização dos postos industriais
          </div>

          <!-- Dados de resumo -->
          <div class="map-stats">
            <div class="row q-gutter-md justify-center">
              <q-card class="stat-card">
                <q-card-section class="text-center q-pa-md">
                  <div class="text-h4 text-primary">{{ postos.length }}</div>
                  <div class="text-caption text-grey-7">{{ this.$gettext('INDUSTRIAL_MAP_ACTIVE_OUTPOSTS') }}</div>
                </q-card-section>
              </q-card>

              <q-card class="stat-card">
                <q-card-section class="text-center q-pa-md">
                  <div class="text-h4 text-positive">{{ postosOK }}</div>
                  <div class="text-caption text-grey-7">{{ this.$gettext('INDUSTRIAL_MAP_NORMAL') }}</div>
                </q-card-section>
              </q-card>

              <q-card class="stat-card">
                <q-card-section class="text-center q-pa-md">
                  <div class="text-h4 text-warning">{{ postosAlerta }}</div>
                  <div class="text-caption text-grey-7">{{ this.$gettext('INDUSTRIAL_MAP_ALERT') }}</div>
                </q-card-section>
              </q-card>

              <q-card class="stat-card">
                <q-card-section class="text-center q-pa-md">
                  <div class="text-h4 text-negative">{{ postosCriticos }}</div>
                  <div class="text-caption text-grey-7">{{ this.$gettext('INDUSTRIAL_MAP_CRITICAL') }}</div>
                </q-card-section>
              </q-card>
            </div>
          </div>

          <!-- Lista dos postos quando há dados -->
          <div v-if="postos.length > 0" class="postos-preview q-mt-lg">
            <div class="text-subtitle2 q-mb-md text-grey-8">
              {{ $gettext('INDUSTRIAL_MAP_MONITORED_OUTPOSTS') }}
            </div>
            <q-scroll-area style="height: 200px;">
              <q-list class="rounded-borders">
                <q-item
                  v-for="posto in postos.slice(0, 5)"
                  :key="posto.id"
                  clickable
                  @click="onPostoClick(posto)"
                  class="posto-item"
                >
                  <q-item-section avatar>
                    <q-icon
                      name="location_on"
                      :color="getCriticalityColor(posto.criticality)"
                      size="md"
                    />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label class="text-weight-medium">{{ posto.name }}</q-item-label>
                    <q-item-label caption class="text-grey-6">
                      {{ $gettext('OCCUPATION') }}: {{ posto.occupancyRate || 0 }}% | {{ $gettext('CLIENT') }}: {{ getClienteName(posto.clientId) }}
                    </q-item-label>
                  </q-item-section>
                  <q-item-section side>
                    <q-badge
                      :color="getCriticalityColor(posto.criticality)"
                      :label="getCriticalityLabel(posto.criticality)"
                    />
                  </q-item-section>
                </q-item>
              </q-list>
              <div v-if="postos.length > 5" class="text-center q-mt-md">
                <q-chip outline color="primary" icon="add">
                  +{{ postos.length - 5 }} {{ $this.$gettext('STATIONS') }}
                </q-chip>
              </div>
            </q-scroll-area>
          </div>

          <!-- Mensagem quando não há dados -->
          <div v-else class="no-data q-mt-lg">
            <q-icon name="info" size="2rem" color="grey-5" />
            <div class="text-body1 text-grey-6 q-mt-md">
              {{ $gettext('INDUSTRIAL_MAP_SELECT_CLIENT') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Component, Vue, Prop } from 'vue-property-decorator'

@Component({
  name: 'IndustrialMapContainer'
})
class IndustrialMapContainer extends Vue {
  @Prop({ type: Array, default: () => [] }) postos
  @Prop({ type: Array, default: () => [] }) clientes
  @Prop({ type: Object, default: null }) clienteSelecionado
  @Prop({ type: Object, default: () => ({}) }) filtrosAtivos

  // Referência para futura integração com Leaflet
  map = null
  mapInitialized = false

  // Computed properties para estatísticas
  get postosOK () {
    return this.postos.filter(p => p.criticality === 'green').length
  }

  get postosAlerta () {
    return this.postos.filter(p => p.criticality === 'yellow').length
  }

  get postosCriticos () {
    return this.postos.filter(p => p.criticality === 'red').length
  }

  getCriticalityColor (criticality) {
    switch (criticality) {
      case 'red': return 'negative'
      case 'yellow': return 'warning'
      case 'green': return 'positive'
      default: return 'grey'
    }
  }

  getCriticalityLabel (criticality) {
    switch (criticality) {
      case 'red': return 'Alto'
      case 'yellow': return 'Médio'
      case 'green': return 'Baixo'
      default: return 'N/A'
    }
  }

  getClienteName (clientId) {
    const cliente = this.clientes.find(c => c.id === clientId)
    return cliente ? cliente.nomeFantasia : 'Cliente não identificado'
  }

  onPostoClick (posto) {
    this.$emit('posto-click', posto)
  }

  beforeDestroy () {
    if (this.map) {
      this.map.remove()
    }
  }
}
export default IndustrialMapContainer
</script>

<style lang="stylus" scoped>
@import '~quasar-variables'

#industrial-map-container
  width: 100%
  height: 100%

  .map-main-container
    width: 100%
    height: 100%
    display: flex
    align-items: center
    justify-content: center
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)
    border-radius: 8px
    padding: 2rem

    .map-center-area
      width: 100%
      max-width: 800px
      text-align: center

      .map-placeholder-content
        .map-stats
          .stat-card
            min-width: 80px
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12)
            transition: box-shadow 0.2s ease

            &:hover
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15)

        .postos-preview
          background: white
          border-radius: 8px
          padding: 16px
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1)

          .posto-item
            border-radius: 4px
            margin-bottom: 4px

            &:hover
              background: rgba($primary, 0.05)

        .no-data
          opacity: 0.7

@media (max-width: 768px)
  #industrial-map-container
    .map-main-container
      padding: 1rem

      .map-center-area
        .map-stats
          .row
            flex-direction: column
            align-items: center

          .stat-card
            width: 100%
            max-width: 200px
</style>
